import React, { useState, useEffect } from 'react';
import Styles, { CenterLoader, Loader, useScreen } from '../../styles';
import { get, set, size, first, isObject, sortBy } from 'global/lib/lodash';
import clsx from 'clsx';
import moment from 'moment';
import { Typography, FormControlLabel, Switch } from '@mui/material';
import { ColorSlider } from '../color-slider';
import useDebounce from '../../hooks/useDebounce';
import useRetrieveData from '../../hooks/useRetrieveData';
import useStyles from './styles';
import { getAccountSetting } from 'redux/actions/userLogin';
import { SETTINGS } from 'feature/admin/account-settings';
import { HeatChart } from './HeatChart';
import { avgItems, sumCount } from './utils';
import { DataChart } from './Chart';
import { Column, Row } from '../row';
import { IconButton } from '../Button';
import { getHeatchartScore } from '../../utils/getDisplayScore';
import { cleanCopy, ensureArray, exportToCsv, mergeDeepIgnoreNulls, uppercaseFirst } from '../../utils';
import { Text } from '../text';
import Modal, { FullScreen } from '../modal';
import Filters from '../../../feature/report/Filters';
import colors from '../../styles/colors';
import Form from '../../components/form';
import { useUser } from '../../hooks/useUser';
import { useSelector } from 'react-redux';
import { buildFilters } from 'global/utils/filter';
import { userHasPermissions } from 'redux/actions/userLogin';
import { FilterChips } from '../filterchips';
import { DateRanges } from '../../lib/filter';
import { getRowDefs } from '../../../feature/report/ReportData/DataReports';
import { unset } from '../../lib/lodash';
import { testNativeSelect } from '../print/print-view';

export const cleanChartFilters = (filter) => {
  const filters = buildFilters(filter, {
    dontFilter: ['ownership', 'quality_type', 'user_id'],
    convertName: {
      realm: 'realm_id',
    },
    dontSort: true,
    includeRealm: true,
  });

  if (get(filters, 'filter.scorecard_id') === 0) {
    set(filters, 'filter.scorecard_id', undefined);
  }

  if (get(filters, 'filter.superset_id') === 0) {
    set(filters, 'filter.superset_id', undefined);
  }

  if (get(filter, 'daterange') !== 'all') {
    const fdr = DateRanges.find(dr => dr.value == filter.daterange);
    set(filters, 'filter.start_date', get(fdr, 'vals.0'))
    set(filters, 'filter.end_date', get(fdr, 'vals.1'))
  }

  return filters;
}

export const Charting = (props) => {
  const { isMobileXS } = useScreen();
  const classes = useStyles({ isMobile: isMobileXS });
  const styles = Styles();
  const { rows, columns, chips, chartSettings } = props;
  const [processing, setProcessing] = useState(false);
  const ranges = getAccountSetting(SETTINGS.assessmentColorScale);
  const [slide, setSlide] = useState(props.slide || ranges || [0, 100]);
  const slideDebounce = useDebounce(slide, 500);

  const isSavedChart = !!props.originalChartData;

  const [modal, setModal] = useState({
    open: false,
    chartIndex: null,
  })
  const [editChart, setEditChart] = useState({
    open: false,
    onClose: () => {
      setEditChart(s => ({ ...s, open: false }))
    },
  })

  const chartGlobalFilters = useSelector((s) => get(s, 'filter.reports.data'));

  const [state, setState] = useState({ 
    showCounts: props.showCounts !== undefined ? props.showCounts : false, 
    useChart: props.useChart !== undefined ? props.useChart : false,
    data: [],
    loading: props.loading,
    chartFilters: null,
    chipFilters: null,
    rows: null,
  });

  const headerRow = state.headerRow || props.headerRow;
  
  const isDate = ['yearly', 'quarterly', 'monthly'].indexOf(columns) > -1;
  
  const [chart, setChart] = useState({
    title: '',
    cols: [],
    tables: [], //array of array of rows
    header: [],
    includeRowAverage: false,
    includeColAverage: false,
    error: undefined,
  });

  const data = state.data;
  const loading = state.loading;

  const hasData = !!size(data) && !!rows && !!columns && !loading && (props.chartIndex !== undefined ? !!get(data, props.chartIndex) : true);

  const getSelectedColumnGroupByColumn = () => {
    let gb = get(props, 'selectedColumn.groupBy');
    if (typeof gb === 'function') {
      return gb(props);
    }
    return gb;
  };

  const setupFilters = () => {
    setState(s => {
      const ocd = get(props.originalChartData, 'config.params.filter', {});
      if (ocd.realm_type) { ocd.realm_type = ensureArray(ocd.realm_type); }
      const chartFilters = mergeDeepIgnoreNulls(ocd, chartGlobalFilters, { daterange: chartGlobalFilters.daterange !== 'all' ? chartGlobalFilters.daterange : get(props.originalChartData, 'config.daterange') });

      if ((chartGlobalFilters.start_date || chartGlobalFilters.end_date) && chartGlobalFilters.daterange == 'all') {
        set(chartFilters, 'daterange', 'all');
      } else if (chartFilters.daterange) {
        unset(chartFilters, 'start_date');
        unset(chartFilters, 'end_date');
      }

      return {
        ...s, 
        chartFilters,
      }
    })
  }

  const getSelectedRow = () => {
    if (props.getSelectedRow) { 
      return props.getSelectedRow('rows', { selected: { rows: state.rows || props.rows } });
    }
    return props.selectedRow;
  }

  useEffect(() => {
    if (props.loading) {
      setChart((c) => ({ ...c, error: undefined }));
      setState(s => ({ ...s, loading: true }));
    } else {
      setState(s => ({ ...s, data: props.data, loading: false }))
    }
  }, [props.loading, props.data]);

  useEffect(() => {
    if (hasData && !loading) {
      if (data.error) {
        return setChart({ ...chart, error: data.error });
      }

      let colArr = [];
      const tableMap = {};
      const includeColAverage = get(props, 'selectedColumn.showColAvg', false) || get(getSelectedRow(), 'showColAvg', false);

      let colKey = isDate ? columns.replace('ly', '') : columns;

      const getCol = (d, setRow = false) => {
        const { month, year } = d;
        const groupBy = getSelectedColumnGroupByColumn() || colKey;

        if (isDate && year) {
          const dateKey = columns.replace('ly', '');
          colKey = `relationships.${dateKey}`;
          if (setRow) {
            let _month = parseInt(month) - 1; //months start at 0 in moment
            _month = isNaN(_month) ? 1 : _month;
            const tme = dateKey === 'quarter' ? moment().month(_month).year(year).startOf('month').toISOString() : moment().month(_month).year(year).startOf(dateKey).toISOString();
            set(d, `relationships.${dateKey}.id`, tme);
          }
        } else {
          colKey = `relationships.${groupBy}`;
        }

        return get(d, colKey);
      };

      const getRow = (d) => {
        const groupBy = get(getSelectedRow(), 'groupBy', state.rows || rows);
        return get(d, `relationships.${groupBy}`);
      };

      const getTableId = (d) => {
        let tableBy = get(getSelectedRow(), 'tableBy');
        if (typeof tableBy === 'function') {
          tableBy = tableBy(props);
        }
        return tableBy ? get(d, `relationships.${tableBy}.id`, 0) : 0;
      };

      const getTableRow = (d) => {
        let tableBy = get(getSelectedRow(), 'tableBy');
        if (typeof tableBy === 'function') {
          tableBy = tableBy(props);
        }
        return get(d, `relationships.${tableBy}`);
      };

      data.map((d) => {
        const row = getRow(d);
        const col = getCol(d, true);

        const rowid = get(row, 'id', 0);
        const colid = get(col, 'id', col);
        const tableid = getTableId(d);

        if (colid !== undefined && !colArr.find((c) => get(c, 'id') === colid)) {
          const name = get(col, 'name', colid || 'none');
          colArr.push({ ...col, name, colKey });
        }

        set(tableMap, [tableid, rowid], [...get(tableMap, [tableid, rowid], []), d]);
      });

      colArr = colArr.sort((a, b) => {
        const isDate = moment(a.id).isValid();

        if (isDate) {
          return moment(a.id) - moment(b.id);
        } else {
          return a.id - b.id;
        }
      });

      const cols = {}; //map of col averages
      const tablerows = {};

      const sortRows = (_rows) => {
        const hasOrder = _rows.find((r) => r.find((rr) => rr.order !== undefined));
        if (hasOrder) {
          return sortBy(_rows, (o) =>
            get(
              (o || []).find((oo) => oo.order !== undefined),
              'order'
            )
          );
        } else {
          return sortBy(_rows, (o) => get(o, '0.value'));
        }
      };

      Object.keys(tableMap).map((k, _i) => {
        const arr = get(tableMap, k, []).filter((a) => a);

        const tabrows = arr.map((ar) => {
          const group = getRow(ar[0]);
          const title = get(group, 'name');
          const tooltip = get(group, 'description');

          const rowcalcs = colArr.map((c, i) => {
            i = includeColAverage ? i + 1 : i;
            const hasVal = ar.find((a) => get(getCol(a), 'id', getCol(a)) === get(c, 'id'));
            const calc = hasVal ? parseFloat(get(hasVal, 'calculated_value', 0)) : 'N/A';
            const count = get(hasVal, 'count');
            //get our column averages
            const configKey = 'relationships.edition.config';
            const ob = {
              value: calc,
              count,
              id: get(c, 'id'),
              order: get(hasVal, 'order'),
              config: get(
                hasVal,
                configKey,
                get(
                  ar.find((a) => get(a, configKey)),
                  configKey
                )
              ),
              relationships: get(hasVal, 'relationships'),
            };
            set(cols, [_i, i], [...get(cols, [_i, i], []), ob]);
            return ob;
          });

          const titleClick = (() => {
            const { tier } = (getSelectedRow() || {});
            const edition = get(ar, '0.relationships.edition');
            if (tier === 'scorecard' || tier === 'superset') {
              return () => {
                let obj = { [`${state.rows || rows}_id`]: { id: get(group, 'id'), name: title } };

                if (!!edition) {
                  obj = {
                    ...obj,
                    ...(isSavedChart ? state.chipFilters : {}),
                    edition_id: { id: get(edition, 'id'), name: get(edition, 'name') },
                  };
                }

                if (isSavedChart) {
                  const srows = get(getRowDefs(getSelectedRow().id), 'rowSelect.rows', state.rows);
                  setState(s => ({ ...s, chipFilters: obj, rows: srows }))
                } else {
                  props.onRowSelect && props.onRowSelect(obj);
                }
              };
            }
          })();

          const rowAvg = {
            rowcalcs,
            value: avgItems(rowcalcs),
            count: sumCount(rowcalcs),
            config: get(
              rowcalcs.find((r) => !!r.config),
              'config'
            ),
          };

          if (includeColAverage) {
            set(cols, [_i, 0], [...get(cols, [_i, 0], []), rowAvg]);
          }

          return [{ value: title, tooltip, onClick: titleClick }, ...[rowAvg], ...rowcalcs];
        });
        const rr = getTableRow(first(first(arr)));
        set(tablerows, _i, {
          title: get(rr, 'name'),
          rows: sortRows(tabrows),
        });
      });

      setChart({
        ...chart,
        colKey,
        cols,
        header: [...['Average'], ...colArr],
        tables: Object.keys(tablerows).map((k) => tablerows[k]),
        includeColAverage,
      });
    }
  }, [data, rows, columns, loading, slideDebounce, headerRow]);

  useEffect(() => {
    setupFilters();
  }, [chartGlobalFilters, modal.open]);

  const loadChartFilters = async (f = state.chartFilters) => {
    try {
      setState(s => ({ ...s, chartFilters: f, loading: true }))
      const ochartcopy = cleanCopy(props.originalChartData);

      const cfilters = state.chipFilters ? props.getChipFilters(state.chipFilters) : {};
      const orow = state.rows || get(ochartcopy, 'config.params.meta.rows');
      // const newRow = state.chipFilters ? get(getRowDefs(get(getSelectedRow(), 'id')), 'rowSelect.rows', orow) : orow;
      const filters = cleanChartFilters({ ...f, ...cfilters });

      const res = await chartSettings.loadChartData(ochartcopy, filters.filter, orow);
      setState(s => ({ ...s, data: res.data, headerRow: res.headerRow }))
    } catch (err) {
      alert(err);
    } finally {
      setState(s => ({ ...s, loading: false }))
    }
  }

  useEffect(() => {
    if (!!state.chipFilters) {
      loadChartFilters();
    }
  }, [state.chipFilters]);

  const exportTo = (index) => {
    try {
      testNativeSelect().then(() => {
        setProcessing(true);
  
        const expRows = [['Title', ...chart.header.map((h) => getHeaderTitle({ val: h, ...state }))]];
  
        const tabs = typeof index === 'number' ? [chart.tables[index]] : chart.tables;
  
        tabs.map((tabl, i) => {
          expRows.push([tabl.title]);
  
          if (chart.includeColAverage) {
            expRows.push([
              headerAvgTitle(tabl, state.showCounts),
              ...get(chart, `cols.${index}`, []).map((col, _i) => {
                const ck = colKey(tabl);
                const foundHeaderRow = findHeaderRow(ck, col);
                const headerRowItems = _i === 0 ? findHeaderRow(ck, get(chart, `cols.${i}`), true) || [] : [];
                const value = ck
                  ? (_i === 0
                    ? avgItems(col)
                    : parseFloat(get(foundHeaderRow, 'calculated_value')))
                  : avgItems(col);
                const count = ck ? (_i === 0 ? headerRowItems.reduce((a, c) => a + get(c, 'count', 0), 0) : get(foundHeaderRow, 'count', 0)) : sumCount(col);
                return getDisplayVal({ ...col, val: { value, config: get(col, '0.config'), count } }).display;
              }),
            ]);
          }
  
          get(tabl, `rows`, [])
            .map((col) => col.map((c) => getDisplayVal({ ...c, val: c }).display))
            .map((r) => expRows.push(r));
        });
  
        const fileName = (() => {
          return `DataExport-${new Date().toISOString()}.csv`;
        })();
  
        exportToCsv(fileName, expRows);
      })
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const changeCounts = () => {
    setState((s) => ({ ...s, showCounts: !s.showCounts }));
  };

  const changeChart = () => {
    setState((s) => ({ ...s, useChart: !s.useChart }));
  };

  const hasRange = slide[0] !== 0 || slide[1] !== 100;

  const getDisplayVal = (row) => {
    let { val } = row;
    const { value, config, tooltip, count } = row.val;

    let isVal = !!isObject(val);
    const hasClick = get(row, 'val.onClick');
    let isLink = false;
    let scoreVal = val;
    let display = val;
    let hcScore;

    if (typeof value === 'string' || typeof value === 'undefined') {
      if (hasClick) {
        isLink = true;
      }
      isVal = false;
      if (get(getSelectedRow(), 'id') === 'checkpoint' && tooltip) {
        display = `${value}: ${tooltip}`;
      } else {
        display = value || 'None';
      }
    } else {
      if (value === -1) {
        isVal = true;
      }
      scoreVal = value;
      hcScore = getHeatchartScore(row.val, config, state.useChart ? slide : undefined);
      display = hcScore;
    }

    if (state.showCounts && count !== undefined) {
      display = count;
    }

    const disableClick = !modal.open && !!props.isSingleChart;

    return {
      isVal,
      isLink,
      hasClick,
      scoreVal,
      display,
      slide,
      hcScore,
      ...(disableClick ? { isLink: false, hasClick: false } : {}),
    };
  };

  const colKey = (tab) => {
    const gb = get(props, 'selectedColumn.groupBy');
    if (!!size(headerRow) && chart.includeColAverage && gb) {
      let ck = get(props, 'selectedColumn.colKey', gb);
      if (typeof ck === 'function') {
        ck = ck({ ...props, selectedRow: getSelectedRow() });
      }
      const useRelId = size(chart.tables) > 1 ? true : false;
      return { useRelId, id: `${ck}_id` };
    }
    return null;
  };

  const headerAvgTitle = (tab, showCounts = false) => {
    if (size(chart.tables) > 1 || isSavedChart) {
      return get(tab, 'title');
    }
    let selRowId = get(getSelectedRow(), 'parent');
    if (selRowId === 'scorecard_header') {
      selRowId = 'scorecard';
    }

    let keyy = get(chips, `${selRowId}_id.name`, selRowId || (showCounts ? 'Total' : 'Average'));
    return uppercaseFirst(keyy);
  };
  
  const getHeaderTitle = (row) => {
    let { val, showCounts } = row;
    const id = get(row, 'val.id');
    const colKey = get(row, 'val.colKey', '');
    const isString = typeof id === 'string';

    if (id && isString && moment(id).isValid()) {
      const format = colKey.endsWith('year') ? 'YYYY' : colKey.endsWith('quarter') ? 'MMM YYYY' : 'L';
      val = moment(id).format(format);
    }
    if (showCounts && val === 'Average') {
      return 'Total';
    }
    return val;
  };

  const findHeaderRow = (ck, val, returnAll = false) => {
    if (!ck || !val || !Array.isArray(headerRow)) {
      return;
    }

    const { id, useRelId } = ck;
    const [key, _id] = id.split('_');
    const valId = returnAll
      ? val.reduce((a, c) => a || c.reduce((ac, cc) => ac || get(cc, useRelId ? `relationships.${key}.id` : 'id'), null), null)
      : val.reduce((a, c) => a || get(c, useRelId ? `relationships.${key}.id` : 'id'), null);
    const filtered = useRelId ? headerRow.filter((hr) => get(hr, id) == valId) : headerRow;
    if (returnAll) {
      return filtered;
    }
    return filtered.find((hr) => get(hr, `${get(props, 'selectedColumn.groupBy', 'realm')}${isDate ? '' : '_id'}`) == get(val, '0.id'));
  };

  const renderBar = (table, index, opts = {}) => {
    opts = {
      includeTitle: false,
      ...opts,
    }
    return (
      <Row style={{ padding: 8 }}>
        {opts.includeTitle && <Text>{table.title}</Text>}
        <Row style={{ marginLeft: 'auto' }}>
          <IconButton onClick={() => exportTo(index)} title="Download Chart Data" loading={processing} icon="save_alt" size="small" variant="none" />
          <IconButton onClick={() => modalChart(table, index)} style={{ transform: 'rotate(45deg)' }} icon="unfold_more" size="small" variant="none" />
        </Row>
      </Row>
    )
  }

  const chartProps = {
    ...props,
    ...state,
    getDisplayVal,
    findHeaderRow,
    headerAvgTitle,
    getHeaderTitle,
    colKey,
    renderBar,

    hasRange,
    slide,
    slideDebounce,
    isDate,
    hasData,
    chart,
    chartGlobalFilters,
  }

  const saveChart = (table, chartIndex) => {
    setEditChart(s => ({
      ...s, 
      open: true,
      title: 'SAVE CHART',
      chart: isSavedChart ? props.originalChartData : {
        name: table.title,
      },
      onSubmit: async (vals, p) => {
        await chartSettings.createAndSave(isSavedChart ? { chart: { ...props.originalChartData, ...vals } } : {
          ...vals,
          chartProps: p.chartProps,
          chartIndex,
        })
        s.onClose();
        props.setAlerts && props.setAlerts({
          message: () => <span>Chart saved! To view/edit saved charts, visit the <a href="/account/data">Account/Data</a> page</span>
        })
      },
    }))
  }

  const modalChart = (table, index) => {
    setModal(s => ({
      ...s,
      open: true,
      title: table.title || props.name,
      onClose: () => {
        setModal(s => ({ ...s, open: false }))
        setState(s => ({ ...s, chipFilters: null, rows: null }));
        if (isSavedChart) {
          setTimeout(() => loadChartFilters(), 200);
        }
      },
      renderEndIcon: () => {
        return (
          <Row style={{ marginLeft: 'auto', color: colors.white }}>
            {userHasPermissions(['manageAccount']) && <IconButton
              style={{ color: colors.white }}
              icon="save"
              onClick={() => saveChart(table, index)}
              variant="none"
              size="large" />}
            <IconButton
              style={{ color: colors.white }}
              icon="save_alt"
              onClick={() => exportTo(index)}
              variant="none"
              size="large" />
          </Row>
        );
      },
      chartIndex: index,
    }))
  }

  const renderSliderBar = (opts = {}) => {
    const showSlider = get(props, 'showSlider', true);

    if (!showSlider && !modal.open) { return null };

    opts = {
      asRow: false,
      ...opts,
    }
    return (
      <div style={{ display: 'flex', flexDirection: opts.asRow && !isMobileXS ? 'row' : 'column' }}>
        <div className={clsx(classes.slideContainer, classes.hidePrint)}>
          <Typography variant={isMobileXS ? "caption" : "subtitle1"} color={isMobileXS ? "textSecondary" : undefined}>Color Scale</Typography>

          <div className={classes.slideWrap}>
            <ColorSlider value={slide} onChange={(e, v) => setSlide(v)} />
          </div>
        </div>
        <Row style={{ flexDirection: isMobileXS ? 'column' : 'row' }}>
          <FormControlLabel
            className={clsx(classes.switch, classes.hidePrint)}
            control={<Switch checked={!!state.showCounts} onChange={changeCounts} />}
            label={state.showCounts ? 'Hide observation counts' : 'Show observation counts'}
          />
          <FormControlLabel
            className={clsx(classes.switch, classes.hidePrint)}
            control={<Switch checked={!!state.useChart} onChange={changeChart} />}
            label={'Use Chart'}
          />
        </Row>
      </div>
    )
  }

  const ChartRender = p => {
    if (!loading && !hasData) {
      return (
        <div className={classes.loadBuffer}>{'No results, try updating the filters to include more data.'}</div>
      );
    }
    return state.useChart ? <DataChart {...p} /> : <HeatChart {...p} />
  }

  const renderChart = () => {
    return ChartRender(chartProps);
  }

  const renderModalChart = () => {
    let filterProps = {};

    if (isSavedChart) {
      filterProps = {
        filter: state.chartFilters,
        onChange: async f => {
          loadChartFilters(f);
        }
      }
    }

    const chipArray = !!state.chipFilters ? Object.keys(state.chipFilters).filter(k => !!state.chipFilters[k]).map(k => ({ ...state.chipFilters[k], key: k })) : null;

    return (
      <div style={{ width: '100%' }}>
        <Row>
          <Filters 
            display="data" 
            asRow={!isMobileXS} 
            vendors={props.vendors} 
            onlyFilters={[
              'startDate', 
              'endDate', 
              'status',
              props.columns !== 'company' ? 'vendors' : null, 
              ...(['builder', 'division', 'community', 'lot'].includes(props.rows) ? ['edition', 'scorecard'] : []),
            ].filter(f => !!f)} 
            {...filterProps}
          />
        </Row>
        {renderSliderBar({ asRow: true })}
        {!!chipArray && <FilterChips filters={chipArray} onDelete={f => setState(s => ({ ...s, chipFilters: { ...s.chipFilters, [f.key]: undefined } }))} />}
        {loading ? <CenterLoader /> : ChartRender({ ...chartProps, chartIndex: modal.chartIndex })}
      </div>
    )
  }

  if (chart.error) {
    return (
      <div className={clsx(classes.loadBuffer)}>{data.error}</div>
    );
  }

  return (
    <>
      {hasData && !loading && renderSliderBar()}
      <div className={clsx(isSavedChart && classes.container, classes.rowAverage)}>
        {!!props.name && <Column center>
          <Text type="title_medium" bold>{props.name}</Text>
          <Text type="title_small">{props.description}</Text>
          <FilterChips includeRealm={false} filter={state.chartFilters} id="reports.data" canDelete={false} dontShow={['ownership']} />
          <div style={{ position: 'absolute', top: 0, right: 0 }}>{renderBar(data, props.chartIndex)}</div>
        </Column>}
        
        {loading && (
          <div className={clsx(styles.centerColumn, classes.loadBuffer)}>
            <Loader size={30} />
          </div>
        )}

        {!loading && renderChart()}
      </div>

      <FullScreen {...modal}>
        {modal.open && renderModalChart()}
      </FullScreen>

      <EditChartModal {...editChart} chartProps={chartProps} />
    </>
  );
};

export const EditChartModal = props => {
  const usergroups = useRetrieveData({ url: `/user-groups` }, { build: ['userGroups'], useCache: { reload: 60 } });
  const { me } = useUser();

  return (
    <Modal {...props}>
      <Form
        showAllScreens
        onSubmit={v => props.onSubmit(v, props)}
        submitText="Submit"
        canSubmit={true}
        submitBtnBoxStyle={{ position: 'relative' }}
        useCheckContinue
        screens={[
          {
            title: '',
            inputs: [
              {
                id: 'name',
                title: 'Title',
                type: 'TextField',
                defaultValue: () => get(props, 'chart.name', ''),
                props: {
                  label: 'Title',
                  required: true,
                }
              },
              {
                id: 'description',
                title: 'Description',
                type: 'TextField',
                defaultValue: () => get(props, 'chart.description', ''),
                props: {
                  label: 'Description',
                }
              },
              {
                id: 'userGroups',
                title: 'User Group',
                type: 'Select',
                defaultValue: () => get(props, 'chart.userGroups', get(me, 'userGroups', []).map(u => `${u.id}`)),
                data: () => usergroups.data,
                item: d => ({ title: d.name, value: d.id }),
                props: {
                  label: 'User Group',
                  multiple: true,
                  required: true
                }
              }
            ]
          }
        ]}
      />
    </Modal>
  )
}